<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import TeamsAPI from 'dashboard/api/teams';
import {
  useMapGetter,
  useStore,
  useStoreGetters,
} from 'dashboard/composables/store';

import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const { currentAccount } = useAccount();

const loading = ref({});
const routingInboxId = ref(null);
const assignmentTeamId = ref(null);
const administratorTeamId = ref(null);
const expandedTeamId = ref(null);
const searchQuery = ref('');
const showDeletePopup = ref(false);
const selectedTeam = ref({});

const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const agentList = computed(() => getters['agents/getAgents'].value);

const inboxAddress = inbox => inbox.email || inbox.name;

const routableInboxes = computed(() =>
  inboxesList.value
    .slice()
    .sort((a, b) => inboxAddress(a).localeCompare(inboxAddress(b)))
);

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;

  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const routedInboxCount = computed(
  () => routableInboxes.value.filter(inbox => inbox.default_team_id).length
);

const routedInboxes = teamId =>
  routableInboxes.value.filter(inbox => inbox.default_team_id === teamId);

const isRoutedToTeam = (inbox, teamId) => inbox.default_team_id === teamId;

const routedTeamName = inbox =>
  teamsList.value.find(team => team.id === inbox.default_team_id)?.name;

const accessGrant = (inbox, userId) =>
  inbox.access_grants?.find(grant => grant.user_id === userId);

const accessLabel = grant => {
  if (!grant) return t('TEAMS_SETTINGS.ACCESS_MATRIX.NONE');

  const teamName = teamsList.value.find(
    team => team.id === grant.source_team_id
  )?.name;
  if (grant.direct_access && teamName) {
    return t('TEAMS_SETTINGS.ACCESS_MATRIX.DIRECT_AND_TEAM', {
      team: teamName,
    });
  }
  if (grant.direct_access) return t('TEAMS_SETTINGS.ACCESS_MATRIX.DIRECT');
  return t('TEAMS_SETTINGS.ACCESS_MATRIX.TEAM', { team: teamName });
};

const accessClass = grant => {
  if (!grant) return 'bg-n-slate-2 text-n-slate-10';
  if (grant.direct_access) return 'bg-n-blue-3 text-n-blue-11';
  return 'bg-n-teal-3 text-n-teal-11';
};

const toggleTeam = teamId => {
  expandedTeamId.value = expandedTeamId.value === teamId ? null : teamId;
};

const routeInbox = async (team, inbox) => {
  const defaultTeamId = isRoutedToTeam(inbox, team.id) ? null : team.id;
  routingInboxId.value = inbox.id;

  try {
    await store.dispatch('inboxes/updateInbox', {
      id: inbox.id,
      formData: false,
      default_team_id: defaultTeamId,
    });
    useAlert(
      defaultTeamId
        ? t('TEAMS_SETTINGS.ROUTING.ROUTE_SUCCESS', {
            address: inboxAddress(inbox),
            team: team.name,
          })
        : t('TEAMS_SETTINGS.ROUTING.UNROUTE_SUCCESS', {
            address: inboxAddress(inbox),
          })
    );
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.ROUTING.ERROR'));
  } finally {
    routingInboxId.value = null;
  }
};

const updateAssignmentMode = async (team, allowAutoAssign) => {
  if (team.allow_auto_assign === allowAutoAssign) return;

  assignmentTeamId.value = team.id;
  try {
    await store.dispatch('teams/update', {
      id: team.id,
      allow_auto_assign: allowAutoAssign,
    });
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.ASSIGNMENT.UPDATE_ERROR'));
  } finally {
    assignmentTeamId.value = null;
  }
};

const toggleTeamAdministrator = async (team, member) => {
  administratorTeamId.value = team.id;
  const administratorIds = team.administrator_ids?.includes(member.id)
    ? team.administrator_ids.filter(id => id !== member.id)
    : [...(team.administrator_ids || []), member.id];

  try {
    await TeamsAPI.updateAgents({
      teamId: team.id,
      agentsList: team.members.map(teamMember => teamMember.id),
      administratorIds,
    });
    await store.dispatch('teams/get');
    useAlert(t('TEAMS_SETTINGS.MEMBERS.ADMIN_UPDATED'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.MEMBERS.ADMIN_UPDATE_ERROR'));
  } finally {
    administratorTeamId.value = null;
  }
};

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);

onMounted(() => {
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!teamsList.length"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('TEAMS_SETTINGS.HEADER')"
        :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="team_management"
      >
        <template v-if="teamsList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_teams_new' }">
            <Button
              :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
              icon="i-lucide-plus"
              size="sm"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        class="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <span class="font-semibold text-n-slate-12">
          {{ $t('TEAMS_SETTINGS.ACCESS_MATRIX.PLATFORM') }}
        </span>
        <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-9" />
        <span>{{ currentAccount?.name }}</span>
        <Icon icon="i-lucide-chevron-right" class="size-4 text-n-slate-9" />
        <span>{{ $t('TEAMS_SETTINGS.ACCESS_MATRIX.BREADCRUMB') }}</span>
      </div>

      <span
        v-if="!filteredTeamsList.length && searchQuery"
        class="flex flex-1 items-center justify-center py-20 text-center text-base text-n-slate-11"
      >
        {{ $t('TEAMS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="border-y border-n-weak">
        <div
          class="hidden grid-cols-[minmax(0,1fr)_9rem_10rem_2rem] items-center gap-4 border-b border-n-weak px-4 py-2 text-xs font-medium uppercase text-n-slate-10 md:grid"
        >
          <span>{{ $t('TEAMS_SETTINGS.TABLE.TEAM') }}</span>
          <span>{{ $t('TEAMS_SETTINGS.TABLE.ROUTED_ADDRESSES') }}</span>
          <span>{{ $t('TEAMS_SETTINGS.TABLE.ASSIGNMENT') }}</span>
          <span />
        </div>

        <article
          v-for="team in filteredTeamsList"
          :key="team.id"
          class="border-b border-n-weak last:border-b-0"
        >
          <button
            type="button"
            class="grid w-full grid-cols-[minmax(0,1fr)_2rem] items-center gap-4 px-4 py-3 text-left hover:bg-n-alpha-1 md:grid-cols-[minmax(0,1fr)_9rem_10rem_2rem]"
            :aria-expanded="expandedTeamId === team.id"
            @click="toggleTeam(team.id)"
          >
            <span class="flex min-w-0 items-center gap-3">
              <span
                class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-base"
              >
                <EmojiIcon
                  v-if="team.icon"
                  :value="team.icon"
                  :color="team.icon_color"
                  class="size-4"
                />
                <Icon
                  v-else
                  icon="i-lucide-inbox"
                  class="size-4 text-n-slate-11"
                />
              </span>
              <span class="min-w-0">
                <span
                  class="block truncate text-sm font-medium capitalize text-n-slate-12"
                >
                  {{ team.name }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{
                    $t('TEAMS_SETTINGS.TABLE.MEMBER_COUNT', {
                      count: team.member_count || 0,
                    })
                  }}
                </span>
              </span>
            </span>

            <span class="hidden text-sm text-n-slate-11 md:block">
              {{
                $t('TEAMS_SETTINGS.TABLE.ROUTED_RATIO', {
                  routed: routedInboxes(team.id).length,
                  total: routableInboxes.length,
                })
              }}
            </span>

            <span class="hidden md:block">
              <span
                class="inline-flex rounded-md px-2 py-1 text-xs font-medium outline outline-1 -outline-offset-1"
                :class="
                  team.allow_auto_assign
                    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
                    : 'bg-n-slate-2 text-n-slate-11 outline-n-weak'
                "
              >
                {{
                  team.allow_auto_assign
                    ? $t('TEAMS_SETTINGS.ASSIGNMENT.ROUND_ROBIN')
                    : $t('TEAMS_SETTINGS.ASSIGNMENT.MANUAL')
                }}
              </span>
            </span>

            <Icon
              icon="i-lucide-chevron-down"
              class="size-4 text-n-slate-10 transition-transform"
              :class="{ 'rotate-180': expandedTeamId === team.id }"
            />
          </button>

          <div
            v-if="expandedTeamId === team.id"
            class="border-t border-n-weak bg-n-alpha-1 px-4 py-5"
          >
            <div class="grid gap-6 lg:grid-cols-2">
              <section class="min-w-0">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ $t('TEAMS_SETTINGS.ROUTING.TITLE') }}
                  </h3>
                  <span class="text-xs text-n-slate-10">
                    {{ routedInboxes(team.id).length }}
                    {{ $t('TEAMS_SETTINGS.ROUTING.CONNECTED') }}
                  </span>
                </div>

                <div
                  v-if="routableInboxes.length"
                  class="divide-y divide-n-weak border-y border-n-weak"
                >
                  <label
                    v-for="inbox in routableInboxes"
                    :key="inbox.id"
                    class="flex cursor-pointer items-center gap-3 py-3"
                  >
                    <Checkbox
                      :model-value="isRoutedToTeam(inbox, team.id)"
                      :disabled="routingInboxId === inbox.id"
                      @change="routeInbox(team, inbox)"
                    />
                    <span class="min-w-0 flex-1">
                      <span class="flex min-w-0 items-center gap-2">
                        <ChannelIcon
                          :inbox="inbox"
                          class="size-4 shrink-0 text-n-slate-10"
                        />
                        <span
                          class="block truncate text-sm font-medium text-n-slate-12"
                        >
                          {{ inboxAddress(inbox) }}
                        </span>
                      </span>
                      <span
                        v-if="
                          inbox.default_team_id &&
                          !isRoutedToTeam(inbox, team.id)
                        "
                        class="block truncate text-xs capitalize text-n-slate-10"
                      >
                        {{
                          $t('TEAMS_SETTINGS.ROUTING.CURRENT_TEAM', {
                            team: routedTeamName(inbox),
                          })
                        }}
                      </span>
                    </span>
                    <Icon
                      v-if="routingInboxId === inbox.id"
                      icon="i-lucide-loader-circle"
                      class="size-4 animate-spin text-n-slate-10"
                    />
                  </label>
                </div>

                <router-link
                  v-else
                  :to="{ name: 'settings_inbox_new' }"
                  class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-11 hover:underline"
                >
                  <Icon icon="i-lucide-plus" class="size-4" />
                  {{ $t('TEAMS_SETTINGS.ROUTING.ADD_CHANNEL') }}
                </router-link>
              </section>

              <section class="min-w-0">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ $t('TEAMS_SETTINGS.MEMBERS.TITLE') }}
                  </h3>
                  <router-link
                    v-if="isAdmin"
                    :to="{
                      name: 'settings_teams_edit_members',
                      params: { teamId: team.id },
                    }"
                    class="text-xs font-medium text-n-blue-11 hover:underline"
                  >
                    {{ $t('TEAMS_SETTINGS.MEMBERS.MANAGE') }}
                  </router-link>
                </div>

                <div
                  v-if="team.members?.length"
                  class="divide-y divide-n-weak border-y border-n-weak"
                >
                  <div
                    v-for="member in team.members"
                    :key="member.id"
                    class="flex items-center gap-3 py-2.5"
                  >
                    <Avatar
                      :src="member.thumbnail"
                      :name="member.name"
                      :status="member.availability_status"
                      :size="24"
                      rounded-full
                    />
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-12"
                    >
                      {{ member.name }}
                    </span>
                    <span class="text-xs capitalize text-n-slate-10">
                      {{ member.availability_status }}
                    </span>
                    <Button
                      v-if="isAdmin"
                      :label="
                        team.administrator_ids?.includes(member.id)
                          ? $t('TEAMS_SETTINGS.MEMBERS.ADMIN')
                          : $t('TEAMS_SETTINGS.MEMBERS.MAKE_ADMIN')
                      "
                      :icon="
                        team.administrator_ids?.includes(member.id)
                          ? 'i-lucide-shield-check'
                          : 'i-lucide-shield-plus'
                      "
                      :is-loading="administratorTeamId === team.id"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      @click="toggleTeamAdministrator(team, member)"
                    />
                  </div>
                </div>
                <p v-else class="mb-0 py-3 text-sm text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.MEMBERS.EMPTY') }}
                </p>
              </section>
            </div>

            <section class="mt-6 border-t border-n-weak pt-5">
              <div class="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                    {{ $t('TEAMS_SETTINGS.ASSIGNMENT.TITLE') }}
                  </h3>
                  <p class="mb-0 text-xs text-n-slate-10">
                    {{
                      $t('TEAMS_SETTINGS.ASSIGNMENT.OPEN_COUNT', {
                        count: team.open_conversations_count || 0,
                      })
                    }}
                  </p>
                </div>

                <div
                  class="inline-flex rounded-lg bg-n-alpha-2 p-1 outline outline-1 -outline-offset-1 outline-n-weak"
                  :class="{
                    'pointer-events-none opacity-60':
                      assignmentTeamId === team.id,
                  }"
                >
                  <button
                    type="button"
                    class="h-7 rounded-md px-3 text-xs font-medium text-n-slate-11"
                    :class="{
                      'bg-n-solid-1 text-n-slate-12 shadow-sm':
                        !team.allow_auto_assign,
                    }"
                    @click="updateAssignmentMode(team, false)"
                  >
                    {{ $t('TEAMS_SETTINGS.ASSIGNMENT.MANUAL') }}
                  </button>
                  <button
                    type="button"
                    class="h-7 rounded-md px-3 text-xs font-medium text-n-slate-11"
                    :class="{
                      'bg-n-solid-1 text-n-slate-12 shadow-sm':
                        team.allow_auto_assign,
                    }"
                    @click="updateAssignmentMode(team, true)"
                  >
                    {{ $t('TEAMS_SETTINGS.ASSIGNMENT.ROUND_ROBIN') }}
                  </button>
                </div>
              </div>
            </section>

            <div
              v-if="isAdmin"
              class="mt-5 flex items-center justify-between border-t border-n-weak pt-4"
            >
              <router-link
                :to="{
                  name: 'settings_teams_edit',
                  params: { teamId: team.id },
                }"
              >
                <Button
                  :label="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                  icon="i-lucide-pencil"
                  size="sm"
                  color="slate"
                  variant="ghost"
                />
              </router-link>
              <Button
                v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                size="sm"
                color="ruby"
                variant="ghost"
                :is-loading="loading[team.id]"
                @click="openDelete(team)"
              />
            </div>
          </div>
        </article>
      </div>

      <div
        v-if="routableInboxes.length"
        class="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-n-slate-10"
      >
        <span>
          {{
            $t('TEAMS_SETTINGS.ROUTING.SUMMARY', {
              routed: routedInboxCount,
              total: routableInboxes.length,
            })
          }}
        </span>
        <router-link
          :to="{ name: 'settings_inbox_list' }"
          class="font-medium text-n-blue-11 hover:underline"
        >
          {{ $t('TEAMS_SETTINGS.ROUTING.MANAGE_CHANNELS') }}
        </router-link>
      </div>

      <section
        v-if="isAdmin && routableInboxes.length"
        class="mt-8 overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <div class="border-b border-n-weak px-4 py-4">
          <h2 class="mb-1 text-base font-semibold text-n-slate-12">
            {{ $t('TEAMS_SETTINGS.ACCESS_MATRIX.TITLE') }}
          </h2>
          <p class="mb-0 text-sm text-n-slate-10">
            {{ $t('TEAMS_SETTINGS.ACCESS_MATRIX.DESCRIPTION') }}
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse text-left text-sm">
            <thead>
              <tr class="bg-n-alpha-1 text-xs uppercase text-n-slate-10">
                <th class="sticky left-0 z-10 min-w-48 bg-n-solid-1 px-4 py-3">
                  {{ $t('TEAMS_SETTINGS.ACCESS_MATRIX.TEAMMATE') }}
                </th>
                <th
                  v-for="inbox in routableInboxes"
                  :key="inbox.id"
                  class="min-w-52 px-4 py-3 font-medium normal-case"
                >
                  {{ inboxAddress(inbox) }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="agent in agentList" :key="agent.id">
                <th
                  class="sticky left-0 z-10 bg-n-solid-1 px-4 py-3 font-medium text-n-slate-12"
                >
                  <span class="flex items-center gap-2">
                    <Avatar
                      :src="agent.thumbnail"
                      :name="agent.name"
                      :size="24"
                      rounded-full
                    />
                    <span class="min-w-0">
                      <span class="block truncate">{{ agent.name }}</span>
                      <span
                        class="block truncate text-xs font-normal text-n-slate-9"
                      >
                        {{ agent.email }}
                      </span>
                    </span>
                  </span>
                </th>
                <td
                  v-for="inbox in routableInboxes"
                  :key="inbox.id"
                  class="px-4 py-3"
                >
                  <span
                    class="inline-flex rounded-md px-2 py-1 text-xs font-medium capitalize"
                    :class="accessClass(accessGrant(inbox, agent.id))"
                  >
                    {{ accessLabel(accessGrant(inbox, agent.id)) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
